import React, { useEffect, useRef } from 'react';
import { renderAsync } from 'docx-preview';

interface DocumentPreviewProps {
  url: string;
  name: string;
}

const DocumentPreview: React.FC<DocumentPreviewProps> = ({ url, name }) => {
  const isPdf = name.toLowerCase().endsWith('.pdf');

  // Hooks must be declared unconditionally
  const containerRef = useRef<HTMLDivElement>(null);

  // Only run DOCX rendering logic when the file is not a PDF
  useEffect(() => {
    if (isPdf) return;

    const loadDocument = async () => {
      if (!containerRef.current) return;

      try {
        const response = await fetch(url);
        const arrayBuffer = await response.arrayBuffer();
        
        // Clear previous content
        containerRef.current.innerHTML = '';
        
        // Render the document
        const options = {
          className: 'docx-preview',
          style: {
            maxWidth: '100%',
            margin: '0 auto',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '4px',
          },
        };
        
        await renderAsync(arrayBuffer, containerRef.current, undefined, options);
      } catch (error) {
        console.error('Error loading document:', error);
        if (containerRef.current) {
          containerRef.current.innerHTML = '<p class="text-red-600">Error loading document. Please try downloading instead.</p>';
        }
      }
    };

    loadDocument();
  }, [url, isPdf]);

  // Render logic
  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-nyc-blue mb-2">{name}</h3>
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {isPdf ? (
          <iframe
            src={url}
            title={name}
            className="w-full"
            style={{ minHeight: '600px' }}
          />
        ) : (
          <div ref={containerRef} className="min-h-[200px]"></div>
        )}
      </div>
      <div className="mt-2">
        <a
          href={url}
          className="text-nyc-orange hover:underline text-sm"
          target="_blank"
          rel="noopener noreferrer"
        >
          Download Original Document
        </a>
      </div>
    </div>
  );
};

export default DocumentPreview; 